import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Col, Form, Input, InputNumber, Modal, Row, Select, Switch } from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { createCase } from "./api";

/** Creates a case and opens it. `initial` may preset { project, client }. */
const NewCaseModal = ({ open, meta, initial, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const program = Form.useWatch("program", form);
  const selected = meta?.programs.find(p => p.key === program);

  useEffect(() => {
    if (!open) return;
    const project = meta?.projects.find(p => p._id === initial?.project);
    form.setFieldsValue({
      program: undefined,
      project: initial?.project || undefined,
      client: initial?.client || project?.client || undefined,
      address:
        meta?.clients.find(c => c._id === (initial?.client || project?.client))?.address || "",
      units: 1,
      ownerOccupied: true
    });
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await createCase({
        program: values.program,
        client: values.client || null,
        project: values.project || null,
        object: {
          address: values.address || "",
          units: values.units || 1,
          ownerOccupied: values.ownerOccupied
        }
      });
      history.push(`/user/funding/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="funding-page"
      open={open}
      width={640}
      title={t("New funding case")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="program"
          label={t("Programme")}
          rules={[{ required: true, message: t("Please choose a funding programme.") }]}
        >
          <Select
            options={(meta?.programs || []).map(p => ({ value: p.key, label: `${p.name}` }))}
            optionRender={option => <span className="fu-wrap">{option.label}</span>}
          />
        </Form.Item>
        {selected && <Alert className="mb-3" type="info" showIcon message={selected.applicant} />}
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="project" label={t("Project")}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                options={(meta?.projects || []).map(p => ({ value: p._id, label: p.name }))}
                onChange={value => {
                  const project = meta.projects.find(p => p._id === value);
                  if (project?.client) {
                    form.setFieldsValue({
                      client: project.client,
                      address:
                        meta.clients.find(c => c._id === project.client)?.address ||
                        form.getFieldValue("address")
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="client" label={t("Customer")}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                options={(meta?.clients || []).map(c => ({ value: c._id, label: c.name }))}
                onChange={value => {
                  const client = meta.clients.find(c => c._id === value);
                  if (client?.address) form.setFieldsValue({ address: client.address });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="address" label={t("Address of the building")}>
          <Input maxLength={300} />
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col xs={12}>
            <Form.Item name="units" label={t("Dwelling units")}>
              <InputNumber className="fu-full" min={1} max={999} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="ownerOccupied" label={t("Owner-occupied")} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default NewCaseModal;
